import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { Block, Delete } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyIcon from '@mui/icons-material/Key';
import WarningIcon from '@mui/icons-material/Warning';
import { Resource } from '../../common/model/type/resource.enum';
import { API_KEY_LIST_FILTERS } from '../../common/model/ui/api-key-list-filters';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import {
  filterApiKeys,
  sortApiKeys,
} from '../../common/utils/services/api-key-list-utils';
import { getColumnSortOptions } from '../../common/utils/services/list-filter-utils';
import { getListRange } from '../../common/utils/services/order-list-utils';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import { useAdminApiKeyMutations } from '../../hooks/use-admin-api-key-mutations';
import { useApiKeyListColumns } from '../../hooks/use-api-key-list-columns';
import useAuthorized from '../../hooks/use-authorized';
import { useListNavigationState } from '../../hooks/use-list-navigation-state';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import ApiKeyService from '../../services/api-key.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvApiKeyList from '../UI/organisms/OvApiKeyList';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';

const AdminApiKeyList: FC = () => {
  const { t } = useTranslation();
  const limit = ApiKeyService.API_KEY_LIST_LIMIT;
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const applicationOperations = useAuthorized(Resource.Application);
  const apiKeyOperations = useAuthorized(Resource.ApiKey);
  const apiKeyCreateAllowed =
    apiKeyOperations.create || applicationOperations.supervise;

  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters,
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  } = useListQueryParams(API_KEY_LIST_FILTERS);

  const {
    data: apiKeysResponse,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => ApiKeyService.getApiKeys(''),
  });

  const { revokeApiKey, activateApiKey, deleteApiKey } =
    useAdminApiKeyMutations();
  const mutationError =
    revokeApiKey.error ?? activateApiKey.error ?? deleteApiKey.error;

  const openRevokeDialog = (apiKeyId: string) => {
    setRevokeReason('');
    setRevokeTargetId(apiKeyId);
  };

  const getNavigationState = useListNavigationState();
  const columns = useApiKeyListColumns({
    getNavigationState,
    onDelete: setDeleteTargetId,
    onRevoke: openRevokeDialog,
    onActivate: activateApiKey.mutate,
  });

  // The backend returns every key and ignores query params, so the page
  // filters, sorts and paginates on the client.
  const matchingApiKeys = sortApiKeys(
    filterApiKeys(apiKeysResponse?.docs ?? [], filterQueryParams),
    orderBy
  );
  const total = matchingApiKeys.length;
  const apiKeys = matchingApiKeys.slice(offset, offset + limit);

  const handleDeleteApiKey = () => {
    if (deleteTargetId) {
      deleteApiKey.mutate(deleteTargetId);
      setDeleteTargetId(null);
    }
  };

  const handleRevokeApiKey = () => {
    if (revokeTargetId) {
      revokeApiKey.mutate({
        id: revokeTargetId,
        reason: revokeReason || undefined,
      });
      setRevokeTargetId(null);
    }
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('apiKeys.resultRange', {
      ...getListRange(offset, limit, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('apiKeys.states.errorTitle')}
          description={t('apiKeys.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (apiKeys.length > 0) {
      return (
        <OvApiKeyList
          apiKeys={apiKeys}
          columns={columns}
          getNavigationState={getNavigationState}
          orderBy={orderBy?.order_by ?? ''}
          orderDir={orderBy?.order_dir ?? ''}
          onSort={changeOrderBy}
        />
      );
    }

    if (hasAppliedFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('apiKeys.states.noResultsTitle')}
          description={t('apiKeys.emptyApiKeyListAfterFiltering')}
        >
          <OvCompactButton onClick={clearFilters}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<KeyIcon />}
        title={t('apiKeys.states.emptyTitle')}
        description={t('apiKeys.emptyApiKeyList')}
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('common.apiKeys')}
      icon={<KeyIcon />}
      headerAction={
        apiKeyCreateAllowed && (
          <NavLink to="/api-keys/create">
            <OvCompactButton icon={<AddCircleIcon />}>
              {t('common.actions.addNew')}
            </OvCompactButton>
          </NavLink>
        )
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        >
          <OvListSortSelect
            options={getColumnSortOptions(columns, t)}
            orderBy={orderBy?.order_by ?? ''}
            orderDir={orderBy?.order_dir ?? ''}
            onChange={changeOrderBy}
          />
        </OvListFilterBar>
      }
      context={
        mutationError && (
          <OvErrorMessage message={getAxiosErrorMessage(mutationError)} />
        )
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={limit}
      offset={offset}
      onPageChange={(_, page) => setOffset((page - 1) * limit)}
      isBusy={
        isFetching ||
        revokeApiKey.isPending ||
        activateApiKey.isPending ||
        deleteApiKey.isPending
      }
    >
      {renderContent()}

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={!!deleteTargetId}
        onCancel={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteApiKey}
        description="apiKeys.actions.deleteConfirm"
        title="common.areYouSure"
      />

      <RevokeConfirmationDialog
        open={!!revokeTargetId}
        onClose={() => setRevokeTargetId(null)}
        aria-labelledby="revoke-dialog-title"
        aria-describedby="revoke-dialog-description"
      >
        <StyledBlockIcon />
        <RevokeDialogTitle id="revoke-dialog-title">
          {t('apiKeys.actions.revoke')}
        </RevokeDialogTitle>
        <RevokeDialogContent>
          <RevokeDialogContentText
            id="revoke-dialog-description"
            textAlign="center"
          >
            {t('apiKeys.actions.revokeReasonPrompt')}
          </RevokeDialogContentText>
          <TextField
            value={revokeReason}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setRevokeReason(event.target.value)
            }
            placeholder={t('apiKeys.actions.revokeReasonPrompt')}
            fullWidth
            multiline
            rows={3}
          />
        </RevokeDialogContent>
        <RevokeDialogActions>
          <RevokeCancelButton onClick={() => setRevokeTargetId(null)}>
            {t('common.actions.cancel')}
          </RevokeCancelButton>
          <RevokeConfirmButton onClick={handleRevokeApiKey} autoFocus>
            {t('apiKeys.actions.revoke')}
          </RevokeConfirmButton>
        </RevokeDialogActions>
      </RevokeConfirmationDialog>
    </OvRecordListPage>
  );
};

export default AdminApiKeyList;

const StyledDeleteIcon = styled(Delete)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const StyledBlockIcon = styled(Block)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const RevokeConfirmationDialog = styled(Dialog)`
  && {
    .MuiDialog-paper {
      border-radius: ${Variables.borderRadius.MEDIUM};
    }
  }
`;

const RevokeDialogTitle = styled(DialogTitle)`
  && {
    text-align: center;
    font-weight: bold;
    font-size: ${Variables.fontSizes.LARGE};
    color: ${Colours.OV_CHARCOAL};
    padding: 1.5rem 1.5rem 0.5rem 1.5rem;
  }
`;

const RevokeDialogContent = styled(DialogContent)`
  && {
    max-width: 26rem;
    padding: 0 1.5rem 1.5rem 1.5rem;
  }
`;

const RevokeDialogContentText = styled(DialogContentText)`
  && {
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.MEDIUM};
    margin-bottom: 1rem;
  }
`;

const RevokeDialogActions = styled(DialogActions)`
  && {
    padding: 0 1.5rem 1.5rem 1.5rem;
    justify-content: flex-end;
    gap: 0.75rem;
  }
`;

const RevokeCancelButton = styled.button`
  && {
    padding: 0.5rem 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const RevokeConfirmButton = styled.button`
  && {
    padding: 0.5rem 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    background-color: ${Colours.OV_BASE};
    border: none;
    color: ${Colours.WHITE};
    font-weight: bold;
    text-transform: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${Colours.OV_CHARCOAL};
    }
  }
`;
